import { BrandLogo } from '../lib/branding';
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import PasswordInput from '../components/PasswordInput';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Building2, Mail, Lock, FileText } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * SignupPage — Inscription simplifiée V4
 * 
 * Champs: SIRET + Email professionnel + Mot de passe + CGU
 * Flow: création → redirect VerifyEmailPage → code 6 chiffres
 */
const SignupPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    siret: '',
    email: '',
    password: '',
    confirmPassword: '',
    cgu_accepted: false
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Validation frontend
  const validate = () => {
    const errs = {};
    
    // SIRET: 14 chiffres
    const siretClean = formData.siret.replace(/[\s-]/g, '');
    if (!siretClean) {
      errs.siret = 'Le SIRET est obligatoire';
    } else if (!/^\d{14}$/.test(siretClean)) {
      errs.siret = 'Le SIRET doit contenir exactement 14 chiffres';
    }
    
    // Email
    if (!formData.email.trim()) {
      errs.email = 'L\'email professionnel est obligatoire';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errs.email = 'Format d\'email invalide';
    }
    
    // Password
    if (!formData.password) {
      errs.password = 'Le mot de passe est obligatoire';
    } else if (formData.password.length < 6) {
      errs.password = 'Le mot de passe doit contenir au moins 6 caractères';
    }
    
    // Confirm password
    if (formData.password !== formData.confirmPassword) {
      errs.confirmPassword = 'Les mots de passe ne correspondent pas';
    }
    
    // CGU
    if (!formData.cgu_accepted) {
      errs.cgu_accepted = 'Vous devez accepter les Conditions Générales d\'Utilisation';
    }
    
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          siret: formData.siret.replace(/[\s-]/g, ''),
          email: formData.email.trim().toLowerCase(),
          password: formData.password,
          cgu_accepted: true
        })
      });
      const data = await res.json();

      if (!res.ok) {
        if (data.detail) {
          toast.error(data.detail);
          if (data.detail.includes('email') || data.detail.includes('Email')) {
            setErrors({ email: data.detail });
          } else if (data.detail.includes('SIRET') || data.detail.includes('siret')) {
            setErrors({ siret: data.detail });
          }
        }
        return;
      }

      toast.success('Compte créé ! Vérifiez votre email.');
      navigate('/verify-email', { state: { email: formData.email.trim().toLowerCase() } });
    } catch {
      toast.error('Erreur serveur. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F5F2] flex" data-testid="signup-page">
      {/* Left: illustration */}
      <div className="hidden lg:flex lg:w-1/2 items-center justify-center p-12 bg-gradient-to-br from-[#1a1a2e] to-[#16213e]">
        <div className="max-w-md text-white space-y-6">
          <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center">
            <Building2 className="h-8 w-8 text-white" />
          </div>
          <h2 className="text-3xl font-bold">
            Rejoignez la plateforme B2B du jeu de société
          </h2>
          <p className="text-white/70 text-lg">
            Accédez aux catalogues de vos fournisseurs, suivez les nouveautés et gérez vos pré-commandes en un seul endroit.
          </p>
          <div className="space-y-3 text-white/60 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center text-xs">1</div>
              <span>Créez votre compte en 30 secondes</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center text-xs">2</div>
              <span>Vérifiez votre email professionnel</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center text-xs">3</div>
              <span>Complétez votre profil et accédez aux conditions</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right: form */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <BrandLogo size="lg" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Créer un compte</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Inscription rapide pour les professionnels du jeu de société
            </p>
          </div>

          {/* Form card */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <form onSubmit={handleSubmit} className="space-y-5">
              
              {/* SIRET */}
              <div className="space-y-2">
                <Label htmlFor="siret" className="flex items-center gap-1">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  SIRET <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="siret"
                  type="text"
                  inputMode="numeric"
                  placeholder="Ex: 12345678900012"
                  value={formData.siret}
                  onChange={e => handleChange('siret', e.target.value)}
                  maxLength={14}
                  className={errors.siret ? 'border-red-500' : ''}
                  data-testid="signup-siret"
                />
                {errors.siret && <p className="text-xs text-red-500">{errors.siret}</p>}
                <p className="text-xs text-muted-foreground">14 chiffres — identifiant unique de votre établissement</p>
              </div>

              {/* Email professionnel */}
              <div className="space-y-2">
                <Label htmlFor="email" className="flex items-center gap-1">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  Email professionnel <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="votremagasin@exemple.fr"
                  value={formData.email}
                  onChange={e => handleChange('email', e.target.value)}
                  className={errors.email ? 'border-red-500' : ''}
                  data-testid="signup-email"
                />
                {errors.email && <p className="text-xs text-red-500">{errors.email}</p>}
              </div>

              {/* Mot de passe */}
              <div className="space-y-2">
                <Label htmlFor="password" className="flex items-center gap-1">
                  <Lock className="h-4 w-4 text-muted-foreground" />
                  Mot de passe <span className="text-red-500">*</span>
                </Label>
                <PasswordInput
                  id="password"
                  placeholder="Minimum 6 caractères"
                  value={formData.password}
                  onChange={e => handleChange('password', e.target.value)}
                  className={errors.password ? 'border-red-500' : ''}
                  data-testid="signup-password"
                />
                {errors.password && <p className="text-xs text-red-500">{errors.password}</p>}
              </div>

              {/* Confirmation mot de passe */}
              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="flex items-center gap-1">
                  <Lock className="h-4 w-4 text-muted-foreground" />
                  Confirmer le mot de passe <span className="text-red-500">*</span>
                </Label>
                <PasswordInput
                  id="confirmPassword"
                  placeholder="Retapez votre mot de passe"
                  value={formData.confirmPassword}
                  onChange={e => handleChange('confirmPassword', e.target.value)}
                  className={errors.confirmPassword ? 'border-red-500' : ''}
                  data-testid="signup-confirm-password"
                />
                {errors.confirmPassword && <p className="text-xs text-red-500">{errors.confirmPassword}</p>}
              </div>

              {/* CGU */}
              <div className="space-y-2">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="cgu"
                    checked={formData.cgu_accepted}
                    onCheckedChange={v => handleChange('cgu_accepted', v)}
                    data-testid="signup-cgu-checkbox"
                  />
                  <Label htmlFor="cgu" className="text-sm leading-relaxed cursor-pointer">
                    J'accepte les{' '}
                    <Link to="/page/cgu" target="_blank" className="text-primary underline hover:text-primary/80">
                      Conditions Générales d'Utilisation
                    </Link>
                    {' '}<span className="text-red-500">*</span>
                  </Label>
                </div>
                {errors.cgu_accepted && <p className="text-xs text-red-500">{errors.cgu_accepted}</p>}
              </div>

              {/* Submit */}
              <Button
                type="submit"
                className="w-full"
                disabled={loading}
                data-testid="signup-submit-button"
              >
                {loading ? 'Création en cours...' : 'Créer un compte'}
              </Button>
            </form>
          </div>

          {/* Already have account */}
          <div className="mt-6 text-center text-sm text-muted-foreground">
            Déjà un compte ?{' '}
            <Link to="/login" className="text-primary font-medium hover:underline">
              Se connecter
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignupPage;
